import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// Admin reply dashboard for V-O question threads. Backs the Questions
// page so admins can answer questions asked to ANY coach — including
// admin-created coaches (Poetry Collection, Goodreads, …) that have no
// owner_user_id and therefore never surface in a creator's app inbox.
//
// Endpoints (legacy admin envelope { success, data, message }):
//   POST /admin/conversation_list   { coach_id?, page, limit }
//   POST /admin/conversation_thread { conversation_id }
//   POST /admin/conversation_reply  { conversation_id, kind, body? }

export type AdminConversation = {
  _id: string
  coach: { id: string; name: string; avatar_url?: string | null } | null
  user: { id: string; name: string; email: string } | null
  last_message_at: string | null
  last_sender: "user" | "coach" | "system"
  message_count: number
  awaiting_coach: boolean
  opened_via?: string | null
  created_at?: string | null
}

export type ConversationListResponse = {
  docs: AdminConversation[]
  totalDocs: number
  page?: number
  totalPages?: number
}

export function useConversationList(params: {
  coach_id?: string
  page: number
  limit: number
}) {
  return useQuery({
    queryKey: ["admin", "conversations", params] as const,
    queryFn: async (): Promise<ConversationListResponse> => {
      const res = await api.post<{
        success: boolean
        data: ConversationListResponse
        message: string
      }>("/admin/conversation_list", params)
      return res.data?.data ?? { docs: [], totalDocs: 0 }
    },
  })
}

export type AdminConversationMessage = {
  _id: string
  sender: "user" | "coach" | "system"
  kind: string
  body?: string
  mediaUrl?: string
  durationSec?: number
  sent_at?: string | null
}

export function useConversationThread(conversationId: string | undefined) {
  return useQuery({
    queryKey: ["admin", "conversations", "thread", conversationId] as const,
    queryFn: async (): Promise<{
      conversation: AdminConversation | null
      messages: AdminConversationMessage[]
    }> => {
      if (!conversationId) return { conversation: null, messages: [] }
      const res = await api.post<{
        success: boolean
        data: {
          conversation: AdminConversation
          messages: AdminConversationMessage[]
        }
        message: string
      }>("/admin/conversation_thread", { conversation_id: conversationId })
      return (
        res.data?.data ?? { conversation: null, messages: [] }
      )
    },
    enabled: !!conversationId,
  })
}

export function useConversationReply() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      conversation_id: string
      kind: "text" | "voice"
      body?: string
    }): Promise<{ success: boolean; message: string }> => {
      const res = await api.post<{ success: boolean; message: string }>(
        "/admin/conversation_reply",
        payload
      )
      return res.data
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "conversations"] })
      qc.invalidateQueries({
        queryKey: ["admin", "conversations", "thread", vars.conversation_id],
      })
    },
  })
}
